import { useState } from "react";
import { SeoHead } from "@/components/SeoHead";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import {
  TIER_PRICING,
  TRIAL_DAYS,
  WORKSPACE_ADDON_PRICE,
  tierDisplayName,
} from "@/lib/tier";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { openBillingPortal } from "@/lib/billing";
import { track } from "@/lib/analyticsEvents";
import {
  TIER_BLURBS,
  TIER_FEATURES,
  TIER_ORDER,
  TRIAL_SET,
  type Billing,
  type PaidTier,
} from "@/components/pricing/pricingConstants";
import { PricingFAQ, PricingFAQJsonLd } from "@/components/pricing/pricingContent";
import { PlanComparisonGrid } from "@/components/pricing/PlanComparisonGrid";

const Pricing = () => {
  const [billing, setBilling] = useState<Billing>("monthly");
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
  const { user } = useAuth();
  const { subscription, isActive } = useSubscription();
  const navigate = useNavigate();

  const currentTier: PaidTier | null =
    isActive && subscription?.product_id
      ? (subscription.product_id.replace("_plan", "") as PaidTier)
      : null;

  const handleCta = async (tier: PaidTier) => {
    if (!user) {
      navigate(`/auth?tab=signup&plan=${tier}_${billing}`);
      return;
    }
    if (isActive && currentTier) {
      await openBillingPortal();
      return;
    }
    setCheckoutPriceId(`${tier}_${billing}`);
    track("checkout_opened", { priceId: `${tier}_${billing}`, context: "pricing" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        title="Pricing — Excerptus"
        description="Excerptus pricing — five plans for writers and researchers building a second brain. 10-day free trial on every plan, card required, nothing charged until day 11."
      />
      <PricingFAQJsonLd />
      <MarketingNav />

      {/* Header */}
      <section className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20">
        <h1 className="font-serif text-4xl font-semibold leading-tight sm:text-5xl">Pricing That Works for the Way You Work.</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Five tiers, from a single workspace to multi-workspace studios. {TRIAL_DAYS}-day free trial on every plan.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          <div className="inline-flex items-center gap-3 rounded-full border border-border bg-transparent px-4 py-2">
            <Label htmlFor="billing-toggle" className={cn("text-sm cursor-pointer", billing === "monthly" ? "text-foreground" : "text-muted-foreground")}>
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={billing === "yearly"}
              onCheckedChange={(c) => setBilling(c ? "yearly" : "monthly")}
              aria-label="Toggle billing period"
              className="pricing-billing-switch"
            />
            <Label htmlFor="billing-toggle" className={cn("text-sm cursor-pointer", billing === "yearly" ? "text-foreground" : "text-muted-foreground")}>
              Yearly
            </Label>
          </div>
          <Badge className="bg-primary text-primary-foreground hover:bg-primary">Save 2 months</Badge>
        </div>
      </section>

      {/* Tier cards */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {TIER_ORDER.map((tier) => {
            const price = TIER_PRICING[tier][billing];
            const isCurrent = currentTier === tier;
            const isFeatured = tier === "pro";
            const hasTrial = TRIAL_SET.has(tier);
            return (
              <div
                key={tier}
                className={cn(
                  "relative flex h-full flex-col rounded-xl border bg-card p-5 shadow-soft",
                  isFeatured ? "mkt-glass-card-featured border-primary ring-2 ring-primary/30" : "border-border",
                )}
              >
                {isFeatured && !isCurrent && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Most popular</Badge>
                )}
                {isCurrent && (
                  <Badge variant="secondary" className="absolute -top-3 left-1/2 -translate-x-1/2">Current</Badge>
                )}
                <div className="min-h-[5.5rem]">
                  <h3 className="font-serif text-xl font-semibold">{tierDisplayName(tier)}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{TIER_BLURBS[tier]}</p>
                </div>
                <div className="flex items-baseline gap-1 pt-5">
                  <span className="font-serif text-3xl font-semibold">${price}</span>
                  <span className="text-sm text-muted-foreground">{billing === "monthly" ? "/mo" : "/yr"}</span>
                </div>
                <Button
                  className="pricing-plan-cta mt-5 w-full"
                  variant={isFeatured ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleCta(tier)}
                  disabled={isCurrent}
                >
                  {isCurrent ? "Your plan" : isActive ? "Switch plan" : hasTrial ? "Start trial" : `Choose ${tierDisplayName(tier)}`}
                </Button>
                <ul className="mt-5 space-y-2 text-sm">
                  {TIER_FEATURES[tier].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Workspace add-on */}
        <div className="mt-8 rounded-xl border border-border bg-card p-5 shadow-soft md:flex md:items-center md:justify-between">
          <div>
            <h3 className="font-serif text-lg font-semibold">Workspace Add-On</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Add an extra workspace to your plan. Billed monthly, no annual option.
              Available on Elite and Agency plans only.
            </p>
          </div>
          <div className="mt-4 md:mt-0 md:text-right">
            <span className="font-serif text-2xl font-semibold">${WORKSPACE_ADDON_PRICE.monthly}&nbsp;</span>
            <span className="text-sm text-muted-foreground">/mo per workspace</span>
          </div>
        </div>
      </section>

      {/* Per-plan features comparison */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="font-serif text-3xl font-semibold text-center">Compare Plans</h2>
        <p className="mt-3 text-center text-sm text-muted-foreground">
          Composer and Knowledge Worker Codex are included on every plan. Limits apply per workspace where relevant.
        </p>
        <PlanComparisonGrid />
      </section>

      <Dialog open={!!checkoutPriceId} onOpenChange={(o) => !o && setCheckoutPriceId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Complete your subscription</DialogTitle>
          </DialogHeader>
          {checkoutPriceId && <StripeEmbeddedCheckout priceId={checkoutPriceId} />}
        </DialogContent>
      </Dialog>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="font-serif text-3xl font-semibold text-center mb-8">Questions</h2>
        <PricingFAQ />
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-muted">
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="font-serif text-3xl font-semibold sm:text-4xl">Give Your Second Brain a Heart</h2>
          <p className="mt-3 text-muted-foreground">
            {TRIAL_DAYS}-day free trial on every plan.
          </p>
          <Link to="/auth/?tab=signup" className="mt-8 inline-block">
            <Button size="lg" className="min-w-40">Start free trial</Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Pricing;
